import { ViewIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ViewOffSlashIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import { FC, useState } from "react";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { cx } from "shared/utils/classnames";
import { SsnData } from "./type";
import { formatMaskedSsn } from "./utils";

interface SsnDisplayProps {
  applicationId?: string;
  applicantId?: string;
  verificationId?: string;
  maskedSsn: string;
  className?: string;
  iconButtonClassName?: string;
  displayValue?: (data: SsnData) => string | undefined;
  fullSsn?: string;
}

export const SsnDisplay: FC<SsnDisplayProps> = ({
  applicationId,
  applicantId,
  verificationId,
  maskedSsn,
  className,
  iconButtonClassName,
  displayValue,
  fullSsn
}) => {
  const {
    permissions: { canShowFullSsn },
    type
  } = useAuthContext();
  const [showFullSsn, setShowFullSsn] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["get-full-ssn", applicationId, applicantId, verificationId],
    queryFn: () =>
      instance
        .get(
          `/portal/v1/applications/${applicationId}/applicants/${applicantId}/verifications/${verificationId}/full_ssn`
        )
        .then((res) => res.data),
    staleTime: 5 * 1000,
    enabled:
      showFullSsn &&
      canShowFullSsn &&
      type.isShowFullSsn &&
      Boolean(applicationId) &&
      Boolean(applicantId) &&
      Boolean(verificationId) &&
      Boolean(verificationId) &&
      !Boolean(fullSsn)
  });

  const getDisplaySsn = () => {
    if (fullSsn) {
      if (showFullSsn) return fullSsn;
      return formatMaskedSsn(fullSsn);
    }

    if (!maskedSsn) return "Not available";
    if (!showFullSsn || !data) return maskedSsn;
    if (displayValue?.(data)) return displayValue(data);
    return data?.ssn ?? "";
  };

  return (
    <div className={cx("flex items-center gap-2 h-5", className)}>
      <p className="text-sm min-w-[90px] gap-1">{getDisplaySsn()}</p>
      {canShowFullSsn && type.isShowFullSsn && Boolean(maskedSsn) ? (
        <IconButton
          className={cx("!text-content-200", iconButtonClassName)}
          size={IconButtonSize.SM}
          variant={IconButtonVariant.TEXT_BASE}
          HugeIcon={showFullSsn ? ViewOffSlashIcon : ViewIcon}
          onClick={() => setShowFullSsn((prev) => !prev)}
          disabled={isLoading}
          loading={isLoading}
        />
      ) : null}
    </div>
  );
};
